import { ArchiveItem } from "models/ArchiveItem";
import { ArchiveNodeOption } from "models/ArchiveNodeOption";
import {
  NODE_TYPE_FOLDER,
  NODE_TYPE_ROOT
} from "views/documents/state/documentArchiveOptions";

/**
 * Set empty state on folders in the tree. If all child nodes of a folder are empty, then the folder will be marked empty
 *
 * @param archiveRoot Root of the archive tree
 * @param emptyFolders Nodes that should be set
 * @returns
 */
export function setEmpty(
  archiveRoot: ArchiveItem[],
  emptyFolders: ArchiveItem[]
) {
  if (emptyFolders.length > 0 && emptyFolders[0].id === NODE_TYPE_ROOT) {
    return;
  }

  emptyFolders.forEach((n) => {
    n.empty = false;

    const filtered = archiveRoot.filter((ar) => ar.id === n.parentId);
    if (filtered.length > 0) {
      setEmpty(archiveRoot, filtered);
    }
  });
}

/**
 * Grabs the archive folders, and generates the folder tree structure, with parents.
 *
 * @param current The current archive option node (in the recursion)
 * @param archiveRoot The root of the archive tree
 * @param currentParentId The current node's parent ID
 * @returns
 */
export function setFoldersParents(
  current: ArchiveNodeOption,
  archiveRoot: ArchiveItem[],
  currentParentId: string
): boolean {
  // If reached a leaf node, set the parent and return
  if (current.nodes.length === 0) {
    // Find the items matching the current node ... ? Should only return one node ... ?
    const items = archiveRoot.filter((ar) => ar.id === current.id);

    if (items.length > 0) {
      items[0].name = current.name;
      items[0].parentId = currentParentId;
      items[0].sortOrder = current.sortOrder;
    }

    return items.length > 0;
  }

  let result = false;

  // Set the parent of all child nodes
  for (const child of current.nodes) {
    const active = setFoldersParents(child, archiveRoot, current.id);
    const items = archiveRoot.filter((ar) => ar.id === current.id);

    // If the child ID matched more than zero items, push a new item to the archive root ... ?
    if (active && items.length === 0) {
      const item: ArchiveItem = {
        id: current.id,
        path: current.id,
        name: current.name,
        header: current.header || false,
        parentId: currentParentId,
        writable: false,
        type: NODE_TYPE_FOLDER,
        empty: true,
        open: false,
        createdAt: new Date(),
        sortOrder: current.sortOrder
      };

      archiveRoot.push(item);
      result = active;
    }
  }

  return result;
}

export function sortEuFolder(archiveRoot: ArchiveItem[]) {
  const filteredArchiveRoot = archiveRoot.filter(
    (f) => f.parentId === "affr-eu"
  );
  const nonEuArchiveRoot = archiveRoot.filter((f) => f.parentId !== "affr-eu");
  filteredArchiveRoot.sort((a, b) => {
    if (a.name > b.name) {
      return -1;
    }
    return 1;
  });

  return [...nonEuArchiveRoot, ...filteredArchiveRoot];
}

/**
 * Set the affr-eu-ovr folder to be the last item in the tree
 *
 * @param archiveRoot
 * @returns
 */
export function setEuovrLast(archiveRoot: ArchiveItem[]) {
  const id = "affr-eu-ovr";
  const euovr = archiveRoot.find((a) => a.id === id);
  if (!euovr) {
    return archiveRoot;
  }

  const nonEuovrArchiveRoot = archiveRoot.filter((b) => b.id !== id);
  return [...nonEuovrArchiveRoot, euovr];
}

/**
 * Sets the "open" state of folders when selecting a new node
 *
 * @param archiveRoot The root of the archive
 * @param folderId The newly selected node ID
 */
export function setOpenFolders(archiveRoot: ArchiveItem[], folderId: string) {
  let nodeToHandle = archiveRoot.filter((ar) => ar.id === folderId)[0];

  while (nodeToHandle?.id !== NODE_TYPE_ROOT) {
    const current = nodeToHandle;
    if (!current) {
      return
    }
    current.open = true;

    const parent = archiveRoot.find((ar) => ar.id === current.parentId);
    if (!parent) {
      throw new Error("Could not locate parent before root");
    }

    nodeToHandle = parent;
  }

  nodeToHandle.open = true;
}
